import { useEffect, useCallback, useRef } from "react";
import { Alert, Platform } from "react-native";
import { useEventSocketContext } from "../../context/EventSocketContext";
import type { UserRole, StreamStatus } from "../EventSocket/types";

interface UseAudienceSocketProps {
  eventId: string;
  userId: string;
  accessToken?: string;
  enabled?: boolean; // Only connect when enabled (e.g., after timer ends)
  showToasts?: boolean; // Enable toast notifications for stream events
}

// Simple toast utility for web/native
const showToast = (message: string, type: "info" | "success" | "warning") => {
  if (Platform.OS === "web" && typeof document !== "undefined") {
    // For web, use a simple styled notification
    const toast = document.createElement("div");
    const colors: Record<string, string> = {
      info: "#3b82f6",
      success: "#10b981",
      warning: "#f59e0b",
    };
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${colors[type]};
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      font-family: -apple-system, BlinkMacSystemFont, sans-serif;
      font-size: 14px;
      font-weight: 500;
      z-index: 10000;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      animation: slideIn 0.3s ease;
    `;
    toast.textContent = message;

    // Add animation style
    const style = document.createElement("style");
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = "slideIn 0.3s ease reverse";
      setTimeout(() => {
        toast.remove();
        style.remove();
      }, 300);
    }, 3000);
  } else if (Platform.OS !== "web") {
    // For native, use Alert
    Alert.alert(type.charAt(0).toUpperCase() + type.slice(1), message);
  }
};

/**
 * useAudienceSocket - Hook for audience socket integration
 *
 * Connects to EventSocket as audience to receive:
 * - Real-time stream status updates for organiser/challenger
 * - Stream events (started, disconnected, reconnected, stopped)
 */
export function useAudienceSocket({
  eventId,
  userId,
  accessToken,
  enabled = true,
  showToasts = true,
}: UseAudienceSocketProps) {
  const {
    connectToEvent,
    disconnectFromEvent,
    fetchInitialState,
    isConnected,
    organiserStatus,
    challengerStatus,
  } = useEventSocketContext();

  // Track previous status to detect changes
  const prevOrganiserStatus = useRef<StreamStatus | null>(null);
  const prevChallengerStatus = useRef<StreamStatus | null>(null);

  // Connect to socket on mount (when enabled)
  useEffect(() => {
    if (!enabled || !eventId || !userId) {
      console.log(
        "[useAudienceSocket] Skipping connection - missing props or disabled"
      );
      return;
    }

    console.log("[useAudienceSocket] Connecting to event:", eventId);
    const role: UserRole = "audience";
    connectToEvent(eventId, userId, role, accessToken);

    // Fetch initial state from NestJS API
    fetchInitialState(eventId);

    // Cleanup on unmount
    return () => {
      console.log("[useAudienceSocket] Disconnecting from event");
      disconnectFromEvent();
    };
  }, [
    enabled,
    eventId,
    userId,
    accessToken,
    connectToEvent,
    disconnectFromEvent,
    fetchInitialState,
  ]);

  // Watch for organiser status changes and show toasts
  useEffect(() => {
    if (!showToasts || !enabled) return;

    const prev = prevOrganiserStatus.current;
    const current = organiserStatus;

    // Skip initial render
    if (prev === null) {
      prevOrganiserStatus.current = current;
      return;
    }

    // Detect status transitions
    if (prev !== current) {
      console.log(`[useAudienceSocket] Organiser: ${prev} → ${current}`);

      // Handle different status transitions
      switch (current) {
        case "live":
          if (prev === "paused") {
            showToast("✅ Organiser stream reconnected!", "success");
          } else {
            showToast("🎬 Organiser stream is now LIVE!", "success");
          }
          break;
        case "paused":
          showToast("⚠️ Organiser stream paused", "warning");
          break;
        case "completed":
          showToast("📴 Organiser stream ended", "info");
          break;
      }
    }

    prevOrganiserStatus.current = current;
  }, [organiserStatus, showToasts, enabled]);

  // Watch for challenger status changes and show toasts
  useEffect(() => {
    if (!showToasts || !enabled) return;

    const prev = prevChallengerStatus.current;
    const current = challengerStatus;

    // Skip initial render
    if (prev === null) {
      prevChallengerStatus.current = current;
      return;
    }

    // Detect status transitions
    if (prev !== current) {
      console.log(`[useAudienceSocket] Challenger: ${prev} → ${current}`);

      // Handle different status transitions
      switch (current) {
        case "live":
          if (prev === "paused") {
            showToast("✅ Challenger stream reconnected!", "success");
          } else {
            showToast("🎬 Challenger stream is now LIVE!", "success");
          }
          break;
        case "paused":
          showToast("⚠️ Challenger stream paused", "warning");
          break;
        case "completed":
          showToast("📴 Challenger stream ended", "info");
          break;
      }
    }

    prevChallengerStatus.current = current;
  }, [challengerStatus, showToasts, enabled]);

  // Manual fetch function for timer callback
  const fetchStatus = useCallback(() => {
    if (eventId) {
      fetchInitialState(eventId);
    }
  }, [eventId, fetchInitialState]);

  return {
    // Connection state
    isConnected,

    // Stream statuses from socket events
    organiserStatus,
    challengerStatus,

    // Derived booleans for convenience
    isOrganiserLive: organiserStatus === "live",
    isChallengerLive: challengerStatus === "live",
    isOrganiserPaused: organiserStatus === "paused",
    isChallengerPaused: challengerStatus === "paused",

    // Manual actions
    fetchStatus,
  };
}
